/*
Copyright (c) 2012-2025 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React, { FC, useEffect, useMemo, useCallback } from "react"
import { Card, Row, Col, Dropdown } from "@duxton-web/ui"
import { MoreVerticalUniversal } from "@duxton-web/icons"
import { useSelector, useDispatch } from "react-redux"
import { actionCreator } from "store/store"
import { AppState } from "store/store.type"
import { SubDepartment } from "types/models/menu"
import { SortableItemList } from "components/common"
import { TroyWebTracker } from "utils/tracking"
import { EventAction, EventCategory } from "constants/tracking"
import { useTranslationsMap } from "common/utils/i18n"
import {
  useMenuV3HierarchiesInfinite,
  flattenInfiniteHierarchies,
} from "queries"
import { FoodmaxapipbEntityType } from "generated"
import { mapHierarchyEntitiesToSubDepartments } from "utils/menuV3Mapper"
import LoadMore from "order-manager/pages/store/list/LoadMore"
import {
  getItemWithoutPhotoCount,
  isItemWithoutPhotoMode,
} from "utils/menu_helper"
import commonStyles from "styles/common.modules.css"
import { MenuSubDepartmentRow } from "./MenuSubDepartmentRow"
import { MenuDepartmentRowLoading } from "./MenuDepartmentRowLoading"
import { MenuPartialError } from "./MenuPartialError"

enum MenuActionKey {
  REARRANGE = "REARRANGE",
  SELECT_MULTIPLE = "SELECT_MULTIPLE",
}

export const MenuOverviewSubDepartmentsV3: FC = () => {
  const dispatch = useDispatch()

  const {
    overview: { department: selectedDepartment },
    merchantID,
    isEditAllowed,
    merchantGroupID,
    menuGroupDetail: { menuGroupID },
    itemFilter,
  } = useSelector((state: AppState) => state?.menu)

  const {
    currentData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useMenuV3HierarchiesInfinite(
    {
      parentType: FoodmaxapipbEntityType.Department,
      parentID: selectedDepartment?.itemClassID,
      merchantID,
      merchantGroupID,
    },
    {
      skip: !selectedDepartment?.itemClassID || !merchantID,
    },
  )

  // Flatten all pages and map to SubDepartment[]
  // Use currentData to ensure we only get data for current query args
  // RTK Query creates separate cache keys for different parentID values
  const subDepartments = useMemo(() => {
    const allEntities = flattenInfiniteHierarchies(currentData)
    return mapHierarchyEntitiesToSubDepartments(
      allEntities || [],
      selectedDepartment?.itemClassID,
      selectedDepartment?.itemClassName,
    )
  }, [currentData, selectedDepartment])

  // Filter sub-departments based on itemFilter
  const filteredSubDepartments = useMemo(() => {
    if (isItemWithoutPhotoMode(itemFilter)) {
      return subDepartments.filter((subDepartment: SubDepartment) => {
        return getItemWithoutPhotoCount(subDepartment.items) !== 0
      })
    }
    return subDepartments
  }, [subDepartments, itemFilter])

  const selectedSubDepartment = useSelector(
    (state: AppState) => state?.menu?.overview?.subDepartment,
  )

  // Auto-select first subdepartment when department is selected
  useEffect(() => {
    if (
      selectedDepartment?.itemClassID &&
      !isLoading &&
      filteredSubDepartments.length > 0
    ) {
      const previouslySelected = selectedSubDepartment
        ? filteredSubDepartments.find(
            (sd: SubDepartment) =>
              sd.itemClassID === selectedSubDepartment.itemClassID,
          )
        : null

      const shouldAutoSelect =
        !previouslySelected || // No previously selected sub-department
        selectedSubDepartment?.departmentID !== selectedDepartment?.itemClassID // Selected department changed

      if (shouldAutoSelect) {
        dispatch(
          actionCreator.menu.selectSubDepartment(
            previouslySelected ?? filteredSubDepartments[0],
          ),
        )
      }
    }
  }, [
    selectedDepartment,
    isLoading,
    filteredSubDepartments,
    selectedSubDepartment,
    dispatch,
  ])

  const textMap = useTranslationsMap({
    rearrange: "menu.actions.rearrange",
    selectMultiple: "menu.actions.selectMultiple",
    subCategoriesTitle: "menu.headings.subCategories",
  })

  const isItemSelected = (subDepartment: SubDepartment) =>
    subDepartment.itemClassID === selectedSubDepartment?.itemClassID

  // Load more handler
  const handleLoadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }

  const handleMenuClick = useCallback(
    (key: MenuActionKey) => {
      switch (key) {
        case MenuActionKey.REARRANGE:
          dispatch(
            actionCreator.menu.toggleDrawer({
              showSortSubDepartments: true,
            }),
          )
          break
        case MenuActionKey.SELECT_MULTIPLE:
          dispatch(
            actionCreator.menu.toggleDrawer({
              showItemSelection: true,
            }),
          )
          break
        default:
          break
      }

      TroyWebTracker.sendEvent({
        category: EventCategory.menuOverviewTab,
        action: EventAction.clickSubDepartmentMore,
        labels: {
          ...(merchantID
            ? { STORE_ID: merchantID }
            : {
                MERCHANT_GROUP_ID: merchantGroupID,
                MENU_GROUP_ID: menuGroupID,
              }),
          NUM_SUB_DEPARTMENT: filteredSubDepartments?.length ?? 0,
          VALUE: key,
        },
      })
    },
    [
      dispatch,
      merchantID,
      merchantGroupID,
      menuGroupID,
      filteredSubDepartments,
    ],
  )

  const handleSelectSubDepartment = (
    _: React.MouseEvent<HTMLElement>,
    subDepartment: SubDepartment,
  ) => {
    dispatch(actionCreator.menu.selectSubDepartment(subDepartment))

    TroyWebTracker.sendEvent({
      category: EventCategory.menuOverviewTab,
      action: EventAction.clickSubDepartmentName,
      labels: {
        ...(merchantID
          ? { STORE_ID: merchantID }
          : { MERCHANT_GROUP_ID: merchantGroupID, MENU_GROUP_ID: menuGroupID }),
        SUB_DEPARTMENT_ID: subDepartment.itemClassID,
      },
    })
  }

  const canShowRearrange =
    isEditAllowed &&
    filteredSubDepartments.length > 0 &&
    selectedSubDepartment !== null

  const handleMenuClickWithKey = (key: MenuActionKey) => () =>
    handleMenuClick(key)

  const menuItems = [
    ...((canShowRearrange && [
      {
        key: MenuActionKey.REARRANGE,
        label: textMap.rearrange,
        onClick: handleMenuClickWithKey(MenuActionKey.REARRANGE),
      },
    ]) ||
      []),
    {
      key: MenuActionKey.SELECT_MULTIPLE,
      label: textMap.selectMultiple,
      onClick: handleMenuClickWithKey(MenuActionKey.SELECT_MULTIPLE),
    },
  ]

  const renderRow = (subDepartment: SubDepartment) => {
    return <MenuSubDepartmentRow subDepartment={subDepartment} />
  }

  const renderLoadMore = () => {
    if (hasNextPage && !error) {
      return (
        <LoadMore
          onLoadMore={handleLoadMore}
          component={<MenuDepartmentRowLoading count={3} />}
        />
      )
    }
    return null
  }

  // Show loading state when:
  // 1. Initial load (isLoading) - no data for current query args yet
  // 2. Fetching but no currentData - means we're fetching for new query args
  // 3. No department selected
  // 4. Error exists but we're fetching (user clicked retry) - show loading immediately
  // Note: RTK Query creates separate cache keys for different parentID values,
  // so currentData will be undefined for new cache keys until fetched
  if (
    isLoading ||
    (isFetching && !currentData) ||
    !selectedDepartment?.itemClassID ||
    (error && isFetching)
  ) {
    return (
      <Card
        className={`${commonStyles.menuTableCard} ${commonStyles.center}`}
        title={
          <Row justify="space-between">
            <Col>{textMap.subCategoriesTitle}</Col>
          </Row>
        }
      >
        <MenuDepartmentRowLoading />
      </Card>
    )
  }

  // Show error state - if there's an error and we're not fetching, show only the error
  if (error) {
    return (
      <Card
        className={`${commonStyles.menuTableCard} ${commonStyles.center}`}
        title={
          <Row justify="space-between">
            <Col>{textMap.subCategoriesTitle}</Col>
          </Row>
        }
      >
        <MenuPartialError error={error} onRefetch={refetch} />
      </Card>
    )
  }

  return (
    <Card
      className={`${commonStyles.menuTableCard} ${commonStyles.center}`}
      title={
        <Row justify="space-between">
          <Col>{textMap.subCategoriesTitle}</Col>
          {isEditAllowed && (
            <Col>
              <Dropdown
                data-testid="menu-button-dropdown"
                menu={{ items: menuItems }}
                trigger={["click"]}
              >
                <MoreVerticalUniversal />
              </Dropdown>
            </Col>
          )}
        </Row>
      }
    >
      <SortableItemList
        items={filteredSubDepartments}
        renderRowContent={renderRow}
        renderLoadMore={renderLoadMore}
        isItemSelected={isItemSelected}
        onRowClick={handleSelectSubDepartment}
        useDragHandle
      />
    </Card>
  )
}
